import { BehaviorSubject, combineLatest, distinctUntilKeyChanged, filter, map, Observable, Observer, shareReplay, Subject, switchMap, take, tap } from 'rxjs';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BusinessThemeService } from '@aston/themes';
import { inject, Injectable } from '@angular/core';

import { objectToStyleAttribute } from '../functions/utils.ai';
import { ResumeConversation } from '../models/ai-chat-history.model';
import { ChatBotResponse } from '../models/ai-response.model';
import { AI_API_ROOT, AI_CURRENT_USER, AI_INTRO_TEXT } from '../di';

import { AIChatboxSuggestionsService } from './ai-chatbox-suggestions.service';
import { OboeClient } from './http-stream.service';

export const AI_CHAT_CHARACTER_LIMIT = 500 - 100; // for app context
export const AI_CHAT_EMPTY_RESPONSE_TIMEOUT = 100; // when AVA is not responding
export const AI_CHAT_PARTIAL_RESPONSE_TIMEOUT = 8000; // when AVA is hanging

export type AiStreamEvent =
	| { type: 'delta', response: ChatBotResponse }
	| { type: 'tool', toolName: string, response: ChatBotResponse }
	| { type: 'done', fullText: string, response: ChatBotResponse }
	| { type: 'error', error: unknown }
	| { type: 'rejected', reason: string }

@Injectable({ providedIn: 'root' })
export class AIChatboxService {
	sugg = inject(AIChatboxSuggestionsService)
	theme = inject(BusinessThemeService)
	i18n = inject(TranslateService)
	http = inject(OboeClient)
	api$ = inject(AI_API_ROOT)
	introText$ = inject(AI_INTRO_TEXT)

	protected _loading$ = new BehaviorSubject(false)
	public loading$ = this._loading$.asObservable()

	protected _onUserMessage$ = new Subject<ChatBotResponse>()
	public onUserMessage$ = this._onUserMessage$.asObservable()

	protected _onResponse$ = new Subject<ChatBotResponse>()
	public onResponse$ = this._onResponse$.asObservable()

	protected _onFinalResponse$ = new Subject<ChatBotResponse>()
	public onFinalResponse$ = this._onFinalResponse$.asObservable()

	protected restart$ = new BehaviorSubject(null)
	public onRestart$ = this.restart$.asObservable()

	protected currentThread$ = new BehaviorSubject<{ id?: string, contextId?: string, lastMessageId?: string }>({})
	currentUser$ = inject(AI_CURRENT_USER)

	protected _resume$ = new Subject<ResumeConversation>()
	resume$ = this._resume$.asObservable()

	hello$ = combineLatest([
		this.currentUser$.pipe(filter(Boolean), distinctUntilKeyChanged('id')),
		this.restart$,
	]).pipe(
		map(([user]) => user),
		switchMap(user => this.i18n.stream('Home.Page.Hello', { name: user.fullName.firstName })),
		shareReplay()
	);

	intro$ = combineLatest([
		this.hello$,
		this.introText$.pipe(tap(intro => console.debug(`[ai-chat] intro text:`, intro))),
		this.theme.getThemed('aiChat')
	]).pipe(
		map(([hello, intro, themeStyle]) => [
			[
				`<div style="${objectToStyleAttribute(themeStyle.intro)}">`,
				`<div style="margin: 2em 0; font-weight: bold; font-size: 1.5em;">${hello}</div>`,
				`<p style="margin-bottom: 1em; white-space: preserve;">${intro}</p>`,
				`</div>`
			].join(''),
		])
	)

	// Sends a question and streams back deltas as they arrive from the backend.
	// The backend streams a chunked JSON array of ChatBotResponseModel; each array element is a text delta.
	// There is no explicit end-of-message marker, so completion is detected via idle/hang watchdog timers.
	sendMessage(question: string): Observable<AiStreamEvent> {
		return new Observable((observer: Observer<AiStreamEvent>) => {
			if (question.length > AI_CHAT_CHARACTER_LIMIT) {
				observer.next({
					type: 'rejected',
					reason: this.i18n.instant('Forms.FieldErrorMessages.MaxLengthPlural', { count: AI_CHAT_CHARACTER_LIMIT }),
				})
				observer.complete()
				return;
			}

			this._loading$.next(true)

			let partialResponseTimeout: ReturnType<typeof setTimeout> | null = null;
			let closed = false;
			const close = () => {
				if (closed) return;
				closed = true;
				clearTimeout(partialResponseTimeout)
				this._loading$.next(false)
				observer.complete()
			}

			const startClosingTimer = () => {
				console.debug(`[ai-chat] start partial response timeout timer`);
				partialResponseTimeout = setTimeout(() => close(), AI_CHAT_PARTIAL_RESPONSE_TIMEOUT);
			}

			let lastResp: ChatBotResponse | undefined;
			let accumulatedText = '';

			const subscription = this.postMessage(question).subscribe({
				next: (event: HttpEvent<string>) => {
					clearTimeout(partialResponseTimeout);

					if (event.type === HttpEventType.Response) {
						if (!Array.isArray(event.body)) return;
						const resp = (event.body as unknown as ChatBotResponse[]).slice(-1).pop()
						if (!resp) {
							console.debug(`[ai-chat] missing response`)
							startClosingTimer();
							return;
						}
						this.currentThread$.next({
							id: resp.threadId,
							contextId: resp.contextId,
						})
						if (resp.kind === 'tool') {
							observer.next({ type: 'tool', toolName: resp.toolName ?? '', response: resp })
							startClosingTimer();
							return;
						}
						if (resp.text) {
							lastResp = resp
							accumulatedText += resp.text
							this._onResponse$.next(resp)
							observer.next({ type: 'delta', response: resp })
							startClosingTimer();
						} else {
							setTimeout(() => close(), AI_CHAT_EMPTY_RESPONSE_TIMEOUT)
						}
					}
				},
				complete: () => {
					console.debug(`[ai-chat] chat response completed`)
					if (lastResp && accumulatedText) {
						const finalResponse = { ...lastResp, text: accumulatedText }
						this._onFinalResponse$.next(finalResponse)
						observer.next({ type: 'done', fullText: accumulatedText, response: finalResponse })
					}
					setTimeout(() => close(), AI_CHAT_EMPTY_RESPONSE_TIMEOUT)
				},
				error: (error) => {
					console.debug(`[ai-chat] chat response error`, error)
					observer.next({ type: 'error', error })
					close()
				}
			});

			return () => {
				clearTimeout(partialResponseTimeout)
				subscription.unsubscribe()
			}
		})
	}

	protected postMessage(question: string): Observable<HttpEvent<string>> {
		const message = this.hydrateRequest({
			contextId: this.currentThread$.value?.contextId,
			threadId: this.currentThread$.value?.id,
			question,
			languageCode: this.i18n.currentLang,
		})
		this._onUserMessage$.next(message)
		return this.api$.pipe(
			take(1),
			switchMap(url => this.http.post(
				`${url}/api/AiChat/chatstreaming`, message,
				{
					observe: "events",
					responseType: "text",
					reportProgress: true,
				}
			))
		)
	}

	startNewThread() {
		this.currentThread$.next({})
		this.restart$.next(null)
	}

	resumeThread(message: ChatBotResponse) {
		this.currentThread$.next({
			contextId: message.contextId,
			id: message.threadId,
		})
	}

	requestResume(conv: ResumeConversation) {
		this._resume$.next(conv)
	}

	// Prefix requests with the current app and page title
	protected hydrateRequest(body: { contextId?: string, threadId?: string, question: string, languageCode: string }) {
		// we previously prefixed questions with locator (ie: Sur l'application Entreprise)
		// it should now be done in the AI chat handler
		return body;
	}
}
